import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Pill } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { Card, Empty, ErrorBox, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import { day, num, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";

export interface StockRow {
  location: string; location_name: string; is_bonded: boolean; sku: string | null; name: string;
  product_class: string | null; qty: number | null; uom: string | null; cases: number | null;
  lot_ref: string | null;
  allocated: number | null; open_sales_order: number | null; inbound: number | null; available: number | null;
  as_of: string; run_id: number;
}
export interface TransitRow { document_id: number; number: string; sku: string | null; name: string; cases: number; shipped: string | null; from: string | null; to: string | null }
interface Matrix {
  locations: { code: string; name: string; is_bonded: boolean; as_of: string | null }[];
  items: { sku: string; name: string; by_location: Record<string, { cases: number | null; as_of: string }>; in_transit: number; duty_paid: number; bonded: number }[];
}
interface Ledger { opening: string | null; closing: string | null; undated_cases: number; items: { sku: string; name: string; opening: number; sold: number; closing: number; implied_inflow: number; in_transit: number | null }[] }

const TABS = [
  { id: "matrix", label: "By product" },
  { id: "lines", label: "Every line" },
  { id: "transit", label: "In transit" },
  { id: "ledger", label: "Ledger" },
];

export function Stock() {
  const [tab, setTab] = useTab(TABS);
  return (
    <>
      <PageHeader title="Stock" sub="Latest reading from each warehouse's own report. Documents never change these figures." />
      <Tabs tabs={TABS} current={tab} onChange={setTab} />
      {tab === "matrix" && <MatrixView />}
      {tab === "lines" && <LinesView />}
      {tab === "transit" && <TransitView />}
      {tab === "ledger" && <LedgerView />}
    </>
  );
}

function MatrixView() {
  const { data, error, reload } = useApi<Matrix>("/stock/matrix");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  return (
    <Card title="Finished goods, cases">
      <div className="table-scroll">
        <table className="grid" data-testid="stock-matrix">
          <thead>
            <tr>
              <th>Product</th>
              {data.locations.map((l) => (
                <th key={l.code} className="n">
                  <Link to={`/locations/${l.code}`}>{l.code}</Link>{l.is_bonded && <> <Pill tone="warning">bonded</Pill></>}
                  <div className="th-sub">{day(l.as_of)}</div>
                </th>
              ))}
              <th className="n">In transit</th>
              <th className="n">Duty paid</th>
              <th className="n">Bonded</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => (
              <tr key={it.sku}>
                <td><Link to={`/products/${it.sku}`}>{it.name}</Link></td>
                {data.locations.map((l) => <td key={l.code} className="n">{num(it.by_location[l.code]?.cases, 2)}</td>)}
                <td className="n">{it.in_transit ? num(it.in_transit) : "—"}</td>
                <td className="n strong">{num(it.duty_paid, 2)}</td>
                <td className="n">{it.bonded ? num(it.bonded, 2) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="note muted">
        Bonded stock (duty unpaid) is never added to the duty-paid total. Locations report on different dates — each column shows its own.
      </p>
    </Card>
  );
}

function LinesView() {
  const [params, setParams] = useSearchParams();
  const location = params.get("location") ?? "";
  const pclass = params.get("class") ?? "";
  const qs = new URLSearchParams();
  if (location) qs.set("location", location);
  if (pclass) qs.set("product_class", pclass);
  const { data, error, reload } = useApi<StockRow[]>(`/stock?${qs}`);
  const locs = useApi<{ code: string; name: string }[]>("/locations");
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n, { replace: true }); };

  if (error) return <ErrorBox error={error} onRetry={reload} />;
  return (
    <Card>
      <StockTable rows={data ?? []} loading={!data} toolbar={
        <>
          <select aria-label="Location" value={location} onChange={(e) => set("location", e.target.value)}>
            <option value="">All locations</option>
            {(locs.data ?? []).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          <select aria-label="Product class" value={pclass} onChange={(e) => set("class", e.target.value)}>
            <option value="">All classes</option>
            <option value="finished">Finished</option><option value="material">Packaging</option>
            <option value="bulk">Bulk liquid</option><option value="merch">Merchandise</option>
          </select>
        </>
      } />
    </Card>
  );
}

export function StockTable({ rows, toolbar, loading, hideLocation }: { rows: StockRow[]; toolbar?: ReactNode; loading?: boolean; hideLocation?: boolean }) {
  if (loading) return <Loading />;
  return (
    <DataTable<StockRow>
      testId="stock-lines" rows={rows} rowKey={(r) => `${r.location}-${r.sku}-${r.run_id}`} toolbar={toolbar}
      href={(r) => (r.sku ? `/products/${r.sku}` : null)}
      columns={[
        ...(hideLocation ? [] : [{ key: "location", label: "Location", render: (r: StockRow) => (
          <><Link to={`/locations/${r.location}`}>{r.location}</Link>{r.is_bonded && <> <Pill tone="warning">bonded</Pill></>}</>) }]),
        { key: "name", label: "Product", render: (r) => (
          <>{r.name}{r.lot_ref && <> <Pill tone="neutral">{r.lot_ref}</Pill></>}</>) },
        { key: "product_class", label: "Class", render: (r) => titleCase(r.product_class) },
        { key: "qty", label: "On hand", numeric: true, render: (r) => num(r.qty, 2) },
        { key: "uom", label: "Unit", render: (r) => r.uom ?? "—" },
        { key: "open_sales_order", label: "Open SO", numeric: true, render: (r) => num(r.open_sales_order, 2) },
        { key: "available", label: "Available", numeric: true, render: (r) => num(r.available, 2) },
        { key: "inbound", label: "Inbound", numeric: true, render: (r) => num(r.inbound, 2) },
        { key: "as_of", label: "As of", render: (r) => day(r.as_of) },
      ]}
    />
  );
}

interface IncomingRow {
  order_no: string; vendor: string | null; to: string | null; to_name: string | null;
  ordered_on: string | null; scheduled_ship: string | null; scheduled_delivery: string | null;
  days_away: number | null; overdue: boolean; status: string | null;
  shipment_status: string | null; open_amount: number | null; seen_on: string | null;
}

/** Orders a supplier is sending in, with the one thing no other source has: a date. */
function IncomingView() {
  const { data } = useApi<IncomingRow[]>("/stock/incoming");
  if (!data || data.length === 0) return null;
  return (
    <Card title="Coming in">
      <p className="note muted">
        From MHW's purchase-order report, emailed every morning. The stock report says how many
        cases are on their way; this is the only place that says when, and from whom.
      </p>
      <DataTable<IncomingRow> testId="incoming-table" rows={data} rowKey={(r) => r.order_no}
        filterPlaceholder={false} columns={[
          { key: "order_no", label: "Order", render: (r) => <code>{r.order_no}</code> },
          { key: "vendor", label: "From", render: (r) => r.vendor ?? "—" },
          { key: "to", label: "To", render: (r) => r.to_name ?? r.to ?? "—" },
          { key: "scheduled_delivery", label: "Due", render: (r) => day(r.scheduled_delivery) },
          { key: "days_away", label: "", render: (r) =>
              r.days_away == null ? "" :
              r.overdue ? <Pill tone="warning">{Math.abs(r.days_away)} days late</Pill> :
              r.days_away === 0 ? <Pill tone="good">today</Pill> :
              <span className="muted">in {r.days_away} days</span> },
          { key: "status", label: "Status", render: (r) =>
              [r.status, r.shipment_status].filter(Boolean).join(" · ") || "—" },
        ]} />
    </Card>
  );
}

function TransitView() {
  const { data, error, reload } = useApi<TransitRow[]>("/stock/in-transit");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  return (
    <>
    <IncomingView />
    <Card title="On the water">
      <p className="note muted">Recorded when a transfer invoice is marked shipped, cleared when it is marked received. Neither warehouse reports these cases while they travel.</p>
      {data.length === 0 ? <Empty>Nothing in transit. Mark a transfer invoice as shipped to record it here.</Empty> : (
        <DataTable<TransitRow> rows={data} rowKey={(r) => `${r.document_id}-${r.sku}`} href={(r) => `/documents/${r.document_id}`}
          filterPlaceholder={false} columns={[
            { key: "number", label: "Transfer", render: (r) => <Link to={`/documents/${r.document_id}`}>#{r.number}</Link> },
            { key: "name", label: "Product" },
            { key: "cases", label: "Cases", numeric: true, render: (r) => num(r.cases) },
            { key: "route", label: "Route", value: (r) => `${r.from} → ${r.to}` },
            { key: "shipped", label: "Shipped", render: (r) => day(r.shipped) },
          ]} />
      )}
    </Card>
    </>
  );
}

function LedgerView() {
  const { data, error, reload } = useApi<Ledger>("/ledger");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  if (!data.opening) return <Empty>The ledger needs two dated inventory workbooks.</Empty>;
  return (
    <Card title={`Ledger, ${day(data.opening)} → ${day(data.closing)}`}>
      <p className="note muted">
        Closing − opening + sold = what must have arrived (new bottlings and receipts), which no source reports directly.
        A negative figure means stock left without a recorded sale. {num(data.undated_cases)} undated cases are excluded.
      </p>
      <DataTable rows={data.items} rowKey={(r) => r.sku} href={(r) => `/products/${r.sku}`} filterPlaceholder={false}
        testId="ledger" columns={[
          { key: "name", label: "Product" },
          { key: "opening", label: "Opening", numeric: true, render: (r) => num(r.opening) },
          { key: "sold", label: "Sold", numeric: true, render: (r) => num(r.sold) },
          { key: "closing", label: "Closing", numeric: true, render: (r) => num(r.closing) },
          { key: "implied_inflow", label: "Implied inflow", numeric: true,
            render: (r) => <span className={r.implied_inflow < 0 ? "bad" : "strong"}>{num(r.implied_inflow)}</span> },
          { key: "in_transit", label: "In transit now", numeric: true, render: (r) => (r.in_transit ? num(r.in_transit) : "—") },
        ]} />
    </Card>
  );
}
